import React, { useContext, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Spacer,
  Text,
  OrderedList,
  ListItem,
  useColorModeValue
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import withLogger, { Logger } from '../libs/logger';
import { ForgotPassword } from '../libs/authentication';
import { EventStateContext } from '../context/EventContext';

type ForgotPasswordFormProps = {
  callback(err: string | null, data?: { email: string }): void;
  log: Logger;
};

function ForgotPasswordForm({ log, callback }: ForgotPasswordFormProps) {
  const [error, setError] = useState('');
  const { slug } = useContext(EventStateContext);

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  async function onForgotPassword(evt: any) {
    const { email } = evt;
    setError('');

    // try to send verification code
    const codeSent = await ForgotPassword({ username: `${email}#/${slug.slice(0, -1)}` }).catch((err) => {
      log.error({ action: 'onForgotPassword:ForgotPassword:error', error: err });
      setError(err.message);
      return false;
    });

    if (!codeSent) {
      return;
    }

    callback(null, { email });
  }

  async function onCancel() {
    setError('');
    callback(null);
  }

  const forgotPasswordSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required')
  });

  return (
    <Formik validationSchema={forgotPasswordSchema} onSubmit={onForgotPassword} initialValues={{ email: '' }}>
      {({ isSubmitting, handleSubmit, handleChange, handleBlur, values, touched, errors }) => (
        <Form noValidate onSubmit={handleSubmit} color='gray.400'>
          {errorMessage}
          <Field name='email'>
            {({ field, form }) => (
              <FormControl mb='8' isInvalid={form.errors.email && form.touched.email}>
                <FormLabel htmlFor='email' srOnly>
                  Email
                </FormLabel>
                <Input
                  {...field}
                  id='email'
                  placeholder='email'
                  bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                  colorScheme='whiteAlpha'
                />
                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
              </FormControl>
            )}
          </Field>

          <Box color={useColorModeValue('blackAlpha.500', 'whiteAlpha.700')} mb='8'>
            <Text mb='2'>How to reset your password:</Text>
            <OrderedList>
              <ListItem>Enter your registered email address.</ListItem>
              <ListItem>Click Reset Password.</ListItem>
              <ListItem>Check your inbox for an email with a verification code.</ListItem>
              <ListItem>Enter the verification code and your new password.</ListItem>
            </OrderedList>
          </Box>
          <Flex mb='8'>
            <Spacer />
            <Button variant='link' colorScheme='brand' onClick={onCancel}>
              Cancel
            </Button>
          </Flex>
          <Button borderRadius='4px' width='100%' type='submit' colorScheme='brand' isLoading={isSubmitting}>
            Reset Password
          </Button>
        </Form>
      )}
    </Formik>
  );
}

export default withLogger(ForgotPasswordForm, 'component:ForgotPasswordForm');
